/**
 * Log di utilizzo dei tool WebMCP.
 *
 * Ogni invocazione di un tool (execute) viene misurata e tracciata su due canali:
 *   1) evento GA4 `webmcp_tool_call` su window.dataLayer (senza argomenti: niente PII);
 *   2) sendBeacon verso l'edge function /api/webmcp-log, che arricchisce il record
 *      (IP, paese, User-Agent, timestamp Europe/Rome) e lo inoltra all'API MSM
 *      sulla rotta /WebMcpLog (cfr. netlify/edge-functions/webmcp-log.js).
 *
 * Il logging e` best-effort: nessun suo errore puo` alterare l'esito del tool
 * (return o throw restano quelli originali) ne` ritardarne la risposta.
 */

const LOG_ENDPOINT = "/api/webmcp-log";
const SESSION_STORAGE_KEY = "webmcp_session_id";
const MAX_ARG_STRING = 200;
const MAX_RESULT_TEXT = 300;

// Chiavi con dati personali sempre oscurate, qualunque sia il tool: rete di
// sicurezza per tool futuri che dimenticassero di dichiarare `redact`.
const DEFAULT_REDACT = [
  "nome",
  "cognome",
  "telefono",
  "email",
  "note",
  "codiceFiscale",
];

const isBrowser = () =>
  typeof window !== "undefined" && typeof navigator !== "undefined";

const truncate = (value, max) => {
  const text = String(value ?? "");
  return text.length > max ? text.slice(0, max) : text;
};

/**
 * Argomenti ripuliti per il log: le chiavi sensibili diventano un flag di
 * presenza (true/false), le stringhe vengono troncate e gli oggetti annidati non
 * vengono serializzati (gli argomenti dei tool sono piatti).
 */
export function sanitizeArgs(args, redact = []) {
  if (!args || typeof args !== "object") return {};
  const redactSet = new Set(
    [...DEFAULT_REDACT, ...redact].map((k) => k.toLowerCase())
  );
  const clean = {};
  for (const [key, value] of Object.entries(args)) {
    if (redactSet.has(key.toLowerCase())) {
      clean[key] = value !== undefined && value !== null && value !== "";
    } else if (typeof value === "string") {
      clean[key] = truncate(value, MAX_ARG_STRING);
    } else if (
      typeof value === "number" ||
      typeof value === "boolean" ||
      value === null
    ) {
      clean[key] = value;
    } else if (Array.isArray(value)) {
      clean[key] = value
        .filter((v) => ["string", "number", "boolean"].includes(typeof v))
        .slice(0, 20)
        .map((v) => (typeof v === "string" ? truncate(v, MAX_ARG_STRING) : v));
    }
    // oggetti annidati / funzioni: scartati
  }
  return clean;
}

/** Primo blocco di testo del risultato MCP, troncato. */
const resultTextOf = (result) => {
  if (typeof result === "string") return truncate(result, MAX_RESULT_TEXT);
  const block = Array.isArray(result?.content)
    ? result.content.find((c) => c && typeof c.text === "string")
    : null;
  return block ? truncate(block.text, MAX_RESULT_TEXT) : "";
};

const randomId = () => {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
  } catch (e) {
    /* ignore */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

/**
 * Id per tab (sessionStorage): permette di correlare le chiamate in sequenza
 * dello stesso agente (es. ricerca -> apri_scheda). Senza storage ne genera uno
 * nuovo per chiamata.
 */
const getSessionId = () => {
  try {
    let id = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!id) {
      id = randomId();
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    }
    return id;
  } catch (e) {
    return randomId();
  }
};

// "native" se il browser espone l'API WebMCP (document.modelContext), "shim" se
// i tool girano nel polyfill installato su navigator.modelContext.
const getRuntime = () => {
  const mc =
    (typeof document !== "undefined" && document.modelContext) ||
    navigator.modelContext;
  return mc && mc.__isShim ? "shim" : "native";
};

const now = () =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();

const sendToBackend = (body) => {
  const json = JSON.stringify(body);
  try {
    if (typeof navigator.sendBeacon === "function") {
      const queued = navigator.sendBeacon(
        LOG_ENDPOINT,
        new Blob([json], { type: "application/json" })
      );
      if (queued) return;
    }
  } catch (e) {
    /* fallback a fetch */
  }
  if (typeof fetch === "function") {
    fetch(LOG_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: json,
      keepalive: true,
    }).catch(() => {});
  }
};

/** Invia il record di log su dataLayer e backend. Non lancia mai. */
export function logToolCall(entry) {
  try {
    if (!isBrowser()) return;

    const pagePath =
      entry.pagePath ?? (window.location ? window.location.pathname : "");

    // In `gatsby develop` (e con __mcpTry) non si sporcano i log reali: il record
    // finisce solo in console. GATSBY_WEBMCP_LOG_DEV=true lo invia comunque (utile
    // con `netlify dev`, l'unico che espone l'edge function /api/webmcp-log).
    if (
      process.env.NODE_ENV === "development" &&
      process.env.GATSBY_WEBMCP_LOG_DEV !== "true"
    ) {
      // eslint-disable-next-line no-console
      console.info("[webmcp-log] (develop, non inviato)", { ...entry, pagePath });
      return;
    }

    if (window.dataLayer && typeof window.dataLayer.push === "function") {
      window.dataLayer.push({
        event: "webmcp_tool_call",
        tool_name: entry.toolName,
        outcome: entry.outcome,
        duration_ms: entry.durationMs,
        page_path: pagePath,
      });
    }

    sendToBackend({
      toolName: entry.toolName,
      sessionId: getSessionId(),
      pagePath,
      runtime: getRuntime(),
      args: entry.args,
      outcome: entry.outcome,
      errorMessage: entry.errorMessage,
      resultText: entry.resultText,
      durationMs: entry.durationMs,
    });
  } catch (e) {
    /* il logging non deve mai emergere */
  }
}

/**
 * Avvolge l'execute di un tool con il logging. Il valore restituito (o l'errore
 * lanciato) e` sempre quello dell'execute originale.
 */
export function withToolLogging(toolName, execute, { redact = [] } = {}) {
  return async (args, ...rest) => {
    const startedAt = now();
    const entry = {
      toolName,
      errorMessage: "",
      resultText: "",
      // Pagina di partenza catturata prima dell'execute: molti tool fanno navigate().
      pagePath: isBrowser() && window.location ? window.location.pathname : "",
    };
    try {
      entry.args = sanitizeArgs(args, redact);
    } catch (e) {
      entry.args = {};
    }

    const finish = () => {
      entry.durationMs = Math.round(now() - startedAt);
      logToolCall(entry);
    };

    let result;
    try {
      result = await execute(args, ...rest);
    } catch (error) {
      entry.outcome = "exception";
      entry.errorMessage = truncate(error && error.message, MAX_RESULT_TEXT);
      finish();
      throw error;
    }

    try {
      entry.outcome = result && result.isError ? "error" : "ok";
      entry.resultText = resultTextOf(result);
      if (entry.outcome === "error") entry.errorMessage = entry.resultText;
    } catch (e) {
      entry.outcome = entry.outcome || "ok";
    }
    finish();
    return result;
  };
}
